import {PeopleOutline} from '@mui/icons-material';
import {FC, useMemo, useState} from 'react';
import {useCan, usePersona} from 'src/permissions/hooks';
import {Box, Button, Typography} from '@mui/material';
import {AddCircle} from '@mui/icons-material';
import {FormattedMessage, useIntl} from 'react-intl';
import {Table} from 'src/components/tables/Table';
import {FilterBar} from 'src/components/common/FilterBar';
import {TableLayout} from 'src/components/tables/TableLayout';
import {ClientListInLocationDetailsProps} from './ClientListInLocationDetails.types';
import {columnConfig} from './ClientListInLocationDetails.tableConfig';
import AMPModal from 'src/components/common/AMPModal';
import {AddClientToLocationForm} from 'src/components/locations/AddClientToLocationForm';
import {NoClients} from 'src/components/tables/NoClients';
import {ClientCard} from './ClientCard';
import {titleSx} from './ClientListInLocationDetails.sx';
import {addRowSx} from 'src/components/tables/TableLayout/TableLayout.sx';
import {ResponsiveTable} from '../../common/ResponsiveTable';
import {CardList} from 'src/components/common/CardList';
import {useAppSelector, useDefineIsRealEstateRole} from 'src/app/hooks';
import {LocationUserInfo} from 'src/types';

export const ClientListInLocationDetails: FC<ClientListInLocationDetailsProps> = ({
                                                                                      users,
                                                                                      companies,
                                                                                      title,
                                                                                      locationId,
                                                                                      businessId,
                                                                                  }) => {
    const persona = usePersona();
    const {authInfo} = useAppSelector((state) => state.common);

    const [open, setOpen] = useState<boolean>(false);
    const intl = useIntl();
    const dialogTitle = intl.formatMessage({id: 'addUser'});
    // Both the delete column and the "add client" button hang off this, and the two are different
    // rights - assigning somebody to a location is an update of the location, removing them a delete.
    const canAddClient = useCan('locations.assignUser');
    const canCreateDeleteUsers = useCan('users.delete');

    const isCompanyUser = persona.isCompany;
    const isEmployeeUser = persona.isEmployee;
    const data = useMemo(() => {
        const companiesWithFlag = companies.map(c => ({...c, isCompanyRow: true}));

        if (isCompanyUser || isEmployeeUser) {
            const currentCompanyId = authInfo?.company?.id || authInfo?.employee?.company?.id;

            const filteredCompanies = companiesWithFlag.filter(company => company.id === currentCompanyId);
            const filteredUsers = users.filter(user => user.company?.id === currentCompanyId);

            return [...filteredUsers, ...filteredCompanies];
        }

        return [...users, ...companiesWithFlag];
    }, [users, companies, authInfo]);

    const canAdd = canAddClient;

    return (
        <>
            <TableLayout
                toolbar={(
                    <FilterBar
                        title={title}
                        icon={<PeopleOutline/>}
                        subtitle={intl.formatMessage({id: 'rowsFound'}, {count: data.length})}
                    />
                )}
                footerPadded={false}
                footer={
                    canAdd && (
                        <Button
                            sx={addRowSx}
                            onClick={() => setOpen(true)}
                        >
                            <AddCircle/>
                            <FormattedMessage id="addClient"/>
                        </Button>
                    )
                }
            >
                <ResponsiveTable
                    desktopElement={
                        <Table
                            sx={{border: 'none'}}
                            rows={data}
                            columns={columnConfig(canCreateDeleteUsers, isEmployeeUser, locationId, businessId)}
                            hideFooter
                            components={{
                                NoRowsOverlay: NoClients,
                            }}
                        />
                    }
                    responsiveElement={
                        <CardList>
                            {/* Every client the table lists, whether or not they uploaded a photo:
                                the avatar decorates the card, it does not decide that there is one -
                                and while it did, a client without one could not be opened at all. */}
                            {(data as Array<LocationUserInfo & {name?: string}>)?.map((item) => (
                                <ClientCard
                                    key={item?.id}
                                    canCreateDeleteUsers={canCreateDeleteUsers}
                                    businessId={businessId}
                                    {...item}
                                    // A company row names itself where a person carries two names,
                                    // which is the same reading the desktop name column does.
                                    firstName={item?.firstName || item?.name || ''}
                                    lastName={item?.lastName || ''}
                                />
                            ))}
                        </CardList>
                    }
                />
            </TableLayout>
            <AMPModal
                handleClose={() => setOpen(false)}
                open={open}
                title={dialogTitle}
                type="md"
                fullWidth
            >
                <AddClientToLocationForm
                    handleClose={() => setOpen(false)}
                    locationId={locationId}
                    businessId={businessId}
                    existClients={data}
                />
            </AMPModal>
        </>
    );
};
